import React, { useEffect, useRef, useState } from "react";
import Step2Form from "../components/Step2Form";
import Step3Form from "../components/Step3Form";
import Step4Form from "../components/Step4Form";
import axiosClient from "../components/api/axiosClient";
import LoadContentSpinner from "../components/Utils/LoadContentSpinner";
import CongratulateContent from "./CongratulateContent";
import buildEventStoreFormData from "../utils/buildEventStoreFormData";

function WizardForm() {

  useEffect(() => {
    document.title = "Formulaire de demande de publication - Events Billetterie";
  }, []);

  const [currentPage, setPage] = useState(2);
  const isSubmittingRef = useRef(false);

  const nextPage = () => {
    setPage((prev) => ++prev);
    scrollToTop();
  };
  const prevPage = () => {
    setPage((prev) => --prev);
    scrollToTop();
  };

  const scrollToTop = () => {
    window.scrollTo(0, 0);
  };

  const [formData, setFormData] = useState({});
  const [tickets, setTickets] = useState([0]);
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState();
  const [isSuccess, setIsSuccess] = useState(false);

  const [ticketsData, setTicketsData] = useState([
    {
      nom: "",
      prix_unitaire: "",
      quantite: "",
      comment: "",
    },
  ]);

  const [eventData, setEventData] = useState({
    titre: "",
    acteur_principal: "",
    categorie: "",
    date_debut: null,
    date_fin: null,
    heure_debut: null,
    lieu: "",
    lien_gps: "",
    type_evenement: {
      populaire: false,
      vedette: false,
    },
    affiche: undefined,
    image_lieu_evenement: undefined,
    description: "",
    date_fin_cloture: null,
    heure_fin_cloture: null,
  });

  const [images, setImages] = useState({
    affiche: null,
    image_lieu_evenement: null,
  });

  const handleTicketsDataChange = (e, index) => {
    e.preventDefault();
    setTicketsData((prevData) => {
      const updatedTicketsData = [...prevData];
      updatedTicketsData[index] = {
        ...updatedTicketsData[index],
        [e.target.name]: e.target.value,
      };
      return updatedTicketsData;
    });
  };

  const handleEventDataChange = (e) => {
    const { name, value, type, checked } = e.target;
    const inputValue = type === "checkbox" ? checked : value;

    setEventData((prevData) => {
      if (type === "checkbox") {
        return {
          ...prevData,
          type_evenement: {
            ...prevData.type_evenement,
            [name]: inputValue,
          },
        };
      }
      return {
        ...prevData,
        [name]: inputValue,
      };
    });

    if (type === "file") setImages({ ...images, [name]: e.target?.files[0] });
  };

  const [formStatus, setFormStatus] = useState("");

  useEffect(() => {
    setFormData({
      ticketsData,
      eventData,
      affiche: images.affiche,
      image_lieu_evenement: images.image_lieu_evenement,
    });
  }, [
    eventData,
    ticketsData,
    images.affiche,
    images.image_lieu_evenement,
  ]);

  const formatApiErrors = (errors) => {
    if (!errors || typeof errors !== "object") {
      return null;
    }

    const messages = Object.values(errors).flat().filter(Boolean);
    return messages.length > 0 ? messages.join(" ") : null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmittingRef.current) {
      return;
    }

    if (!(images.affiche instanceof File)) {
      setValidationError("L'affiche de l'événement est requise.");
      setTimeout(() => setValidationError(""), 7000);
      return;
    }

    isSubmittingRef.current = true;
    setLoading(true);
    setFormStatus("Envoi du formulaire en cours...");
    setValidationError("");

    const payload = buildEventStoreFormData({
      ticketsData,
      eventData,
      affiche: images.affiche,
      image_lieu_evenement: images.image_lieu_evenement,
    });

    try {
      const response = await axiosClient.post("/request/events/store", payload, {
        timeout: 120000,
      });

      const isSubmitted =
        response?.data?.submited === true ||
        response?.data?.submitted === true ||
        response?.data?.success === true;

      if (isSubmitted) {
        setIsSuccess(true);
        return;
      }

      setValidationError(
        response?.data?.message ||
          "Le serveur a répondu, mais la confirmation est incomplète. Vérifiez la page « Validations » avant de renvoyer le formulaire."
      );
      setTimeout(() => setValidationError(""), 10000);
    } catch (error) {
      const response = error?.response;

      if (!response) {
        setValidationError(
          "Erreur réseau ou délai dépassé. Vérifiez la page « Validations » : votre événement a peut-être déjà été enregistré."
        );
      } else if (response.status === 422) {
        const apiMessage =
          formatApiErrors(response.data?.errors) ||
          response.data?.message ||
          "Certaines informations sont invalides. Corrigez le formulaire puis réessayez.";
        setValidationError(apiMessage);
      } else if (response.status >= 500) {
        setValidationError(
          response.data?.message ||
            "Erreur serveur. Vérifiez la page « Validations » avant de soumettre à nouveau : votre demande a peut-être déjà été enregistrée."
        );
      } else {
        setValidationError(
          response.data?.message ||
            "Erreur de traitement. Vérifiez vos événements en validation avant de réessayer."
        );
      }

      setTimeout(() => setValidationError(""), 10000);
    } finally {
      isSubmittingRef.current = false;
      setLoading(false);
      setFormStatus("");
    }
  };

  const renderStep = () => {
    switch (currentPage) {
      case 2:
        return (
          <Step2Form
            images={images}
            setImages={setImages}
            eventData={eventData}
            handleEventDataChange={handleEventDataChange}
            prevPage={prevPage}
            nextPage={nextPage}
          />
        );
      case 3:
        return (
          <Step3Form
            ticketsData={ticketsData}
            setTicketsData={setTicketsData}
            formData={formData}
            setFormData={setFormData}
            setTickets={setTickets}
            tickets={tickets}
            handleTicketsDataChange={handleTicketsDataChange}
            prevPage={prevPage}
            nextPage={nextPage}
          />
        );
      case 4:
        return (
          <Step4Form
            images={images}
            eventData={eventData}
            ticketsData={ticketsData}
            prevPage={prevPage}
            isSubmitting={loading}
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return <LoadContentSpinner status={formStatus} />;
  }

  if (isSuccess) {
    return <CongratulateContent />;
  }

  return (
    <div className='px-4 sm:px-6 lg:px-8 py-8 w-full'>
      <div className='grid grid-cols-12 gap-6'>
        <div className='col-span-full xl:col-span-12 bg-white dark:bg-gray-800 shadow-sm rounded-xl'>
          <form onSubmit={handleSubmit} encType="multipart/form-data">
          <div className="relative flex w-full justify-center">

            {validationError && (
              <div className="text-sm fixed mt-16 top-0 z-10 cf8dr cb8zv cvkq6 clbq0 cuvgf max-w-2xl">
                <div className="flex cm3rx ce4zk c6btv">
                  <div className="flex">
                    <svg
                        className="cbm9w czr3n cq1qg coqgc chfzq shrink-0"
                        width={16}
                        height={16}
                        viewBox="0 0 16 16"
                    >
                        <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm3.5 10.1l-1.4 1.4L8 9.4l-2.1 2.1-1.4-1.4L6.6 8 4.5 5.9l1.4-1.4L8 6.6l2.1-2.1 1.4 1.4L9.4 8l2.1 2.1z" />
                    </svg>
                    <div>
                  {validationError}
                  </div>
                  </div>
                  </div>
              </div>
            )}
          </div>

            {renderStep()}
          </form>
          </div>
        </div>
    </div>
  );
}

export default WizardForm;
